import React, { JSX, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { formatDateTimeToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import {
	LiveOrderAcceptanceFormApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { isExternalOrderDelivery, isWoltDriveAssigned } from "../../../../../../services/orderDelivery";
import {
	LiveOrderDeliveryOptionType,
	defaultDeliveryTimeOptions,
	woltDriveTimeOptions,
} from "../../../../../../services/types";
import LiveOrderDeliveryTimeOption from "./LiveOrderDeliveryTimeOption";

interface Props {
	setSelectedTimeOption: (option: string | number | undefined) => void;
	selectedTimeOption: number | string | undefined;
	form?: UseFormReturn<LiveOrderAcceptanceFormApi>;
	selectedDeliveryOption: LiveOrderDeliveryOptionType | undefined;
	deliveryEstimatingLoading: boolean;
	optionsFor?: LiveOrderDeliveryOptionType[];
	firstEstimatedDeliveryTime?: number;
	order?: LiveOrderApi;
	orderEstimatedPreparationInMinutes?: number;
	skipFirstDeliveryEstimation?: boolean;
	woltDriveScheduleForm?: UseFormReturn<{ scheduled_delivery_at?: string }>;
	handleSelectDeliveryTimeOption?: (option: string | number | undefined) => void;
	displayOptionsGreaterThanSelected?: true;
}

const LiveOrderDeliveryTimeOptions = ({
	setSelectedTimeOption,
	selectedTimeOption,
	form,
	selectedDeliveryOption,
	deliveryEstimatingLoading,
	optionsFor,
	firstEstimatedDeliveryTime,
	order,
	orderEstimatedPreparationInMinutes,
	skipFirstDeliveryEstimation,
	woltDriveScheduleForm,
	handleSelectDeliveryTimeOption,
	displayOptionsGreaterThanSelected,
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const isWoltDriveSelected = selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE;
	const isWoltDrive = isWoltDriveAssigned(order);
	const canChooseDeliveryTimeOption = isExternalOrderDelivery(order) || selectedDeliveryOption;

	return useMemo(() => {
		const getUniqueWoltDriveOptions = () => {
			let options: number[] = [...woltDriveTimeOptions];

			if (skipFirstDeliveryEstimation !== true) {
				if (firstEstimatedDeliveryTime) {
					options = [...new Set([...options, firstEstimatedDeliveryTime])];
				}

				if (
					isWoltDrive &&
					orderEstimatedPreparationInMinutes !== undefined &&
					orderEstimatedPreparationInMinutes >= 0
				) {
					options = [...new Set([...options, orderEstimatedPreparationInMinutes])];
				}
			}

			if (isWoltDrive && displayOptionsGreaterThanSelected && firstEstimatedDeliveryTime) {
				options = options.filter((option) => option >= firstEstimatedDeliveryTime);
			}

			return options.sort((a, b) => a - b);
		};
		const getOptions = () => {
			if (optionsFor) {
				if (optionsFor.includes(LiveOrderDeliveryOptionType.WOLT_DRIVE) && isWoltDriveSelected)
					return getUniqueWoltDriveOptions();
				if (optionsFor.includes(LiveOrderDeliveryOptionType.OWN_DRIVER) && !isWoltDriveSelected)
					return defaultDeliveryTimeOptions;
			}
			return isWoltDriveSelected ? getUniqueWoltDriveOptions() : defaultDeliveryTimeOptions;
		};

		const showScheduledDeliveryAtDatePicker = isWoltDriveSelected && woltDriveScheduleForm;

		return canChooseDeliveryTimeOption ? (
			<div className="position-relative live-order-delivery-time-options">
				{deliveryEstimatingLoading && <div className="overlay" />}
				<h5>
					{!isWoltDriveSelected
						? t("modules.live_order.field.choose_delivery_time.title")
						: t("modules.live_order.field.choose_preparation_time_for_driver.title")}
				</h5>
				<div className="d-flex flex-wrap gap-2 align-items-center live-order-delivery-time-options-container">
					{getOptions().map((option) => (
						<LiveOrderDeliveryTimeOption
							setSelectedTimeOption={setSelectedTimeOption}
							option={option}
							selectedTimeOption={selectedTimeOption}
							key={option.toString()}
							handleSelect={handleSelectDeliveryTimeOption}
						/>
					))}
					{showScheduledDeliveryAtDatePicker && (
						<FormDatePicker
							control={woltDriveScheduleForm!.control}
							name="scheduled_delivery_at"
							errors={woltDriveScheduleForm!.formState.errors}
							allowResetDate
							onApply={(date) => {
								setSelectedTimeOption(date ? formatDateTimeToString(date as Date) : undefined);
							}}
							placeholder={t("modules.live_order.field.execution_at.placeholder")}
							onClear={() => {
								setSelectedTimeOption(undefined);
								woltDriveScheduleForm?.setValue("scheduled_delivery_at", undefined);
							}}
						/>
					)}
					{!isWoltDriveSelected && form && (
						<FormDatePicker
							control={form.control}
							name="pickup_at"
							errors={form.formState.errors}
							allowResetDate
							onApply={(date) => {
								setSelectedTimeOption(date as string | undefined);
							}}
							placeholder={t("modules.live_order.field.execution_at.placeholder")}
						/>
					)}
				</div>
			</div>
		) : (
			<></>
		);
	}, [
		selectedTimeOption,
		setSelectedTimeOption,
		form?.control,
		t,
		form?.formState?.errors,
		selectedDeliveryOption,
		isWoltDriveSelected,
		deliveryEstimatingLoading,
		firstEstimatedDeliveryTime,
		JSON.stringify(optionsFor),
		woltDriveScheduleForm?.control,
		woltDriveScheduleForm?.formState?.errors,
	]);
};

export default LiveOrderDeliveryTimeOptions;
